import { queryOptions, useQueries, useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { api } from "../../../singleton/axios";

export interface InstitutionalOwnership {
  date: string;
  filingDate: string;
  acceptedDate: string;
  cik: string;
  securityCusip: string;
  symbol: string;
  nameOfIssuer: string;
  shares: number;
  titleOfClass: string;
  sharesType: string;
  putCallShare: string;
  value: number;
  link: string;
  finalLink: string;
}

export interface PortfolioDistributionItem {
  id: string;
  label: string;
  value: number; // percentage
  rawValue: number; // dollar value
  count?: number; // for "Other" category - number of holdings aggregated
}

export type PositionChangeType = "NEW" | "CLOSED" | "INCREASED" | "DECREASED";

export interface QuarterOverQuarterChange {
  symbol: string;
  nameOfIssuer: string;
  securityCusip: string;

  currentQuarter: {
    shares: number;
    value: number;
    portfolioPercentage: number;
  } | null;

  previousQuarter: {
    shares: number;
    value: number;
    portfolioPercentage: number;
  } | null;

  changes: {
    shareChange: number;
    shareChangePercent: number;
    portfolioPercentageChange: number;
    portfolioPercentageChangeAbs: number;
    valueChange: number;
  };

  positionType: PositionChangeType;
}

export interface QuarterComparison {
  currentQuarter: {
    year: number;
    quarter: number;
  };
  previousQuarter: {
    year: number;
    quarter: number;
  };
  changes: QuarterOverQuarterChange[];
  summary: {
    totalNewPositions: number;
    totalClosedPositions: number;
    totalIncreasedPositions: number;
    totalDecreasedPositions: number;
    biggestIncrease: QuarterOverQuarterChange | null;
    biggestDecrease: QuarterOverQuarterChange | null;
  };
}

export interface SuperInvestorHoldingsResponse {
  success: boolean;
  data: InstitutionalOwnership[];
  quarter: number;
  year: number;
  fallbackUsed: boolean;
  portfolioDistribution: PortfolioDistributionItem[];
  totalPortfolioValue: number;
  quarterComparison: QuarterComparison | null;
}

const holdingsQueryOptions = (cik: string, enabled = true) =>
  queryOptions({
    queryKey: ["super-investor-holdings", cik],
    queryFn: async () => {
      const response = await api.get<SuperInvestorHoldingsResponse>(
        "/super-investor-holdings",
        { params: { cik } },
      );
      return response.data;
    },
    enabled: enabled && !!cik && cik.length === 10, // Only fetch if CIK is valid
    staleTime: 60 * 60 * 1000,
    gcTime: 2 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      if (isAxiosError(error) && error.response?.status === 404) return false;
      return failureCount < 2;
    },
  });

export const useSuperInvestorHoldings = (cik: string, enabled = true) =>
  useQuery(holdingsQueryOptions(cik, enabled));

export const useSuperInvestorHoldingsList = (ciks: string[]) =>
  useQueries({ queries: ciks.map((cik) => holdingsQueryOptions(cik)) });
